import dayjs from "dayjs"
import { AssignedEmployeeOrderDetail } from "src/app/slices/dashboard.slice"
import { Address } from "src/db/Address"
import { HaulerEmployee } from "src/db/HaulerEmployee"
import { Order, OrderViewModel } from "src/db/Order"
import {
  appConstants,
  defaultEmployeeOrders,
  InvalidateTagsType,
  SortOrderType,
} from "../constants"
import { BaseQueryApi, TagDescription } from "@reduxjs/toolkit/query"
import { ApiToastHandlerOptions } from "../models"
import { showToast } from "../slices"

export const apiToastHandler = async (
  helpers: {
    dispatch: BaseQueryApi["dispatch"]
    queryFulfilled: Promise<any>
  },
  { success }: ApiToastHandlerOptions,
  invalidateTags?: TagDescription<InvalidateTagsType>[],
  invalidateTagsFn?: (tags: TagDescription<InvalidateTagsType>[]) => any,
) => {
  const { dispatch, queryFulfilled } = helpers

  try {
    await queryFulfilled
    if (success) {
      dispatch(
        showToast({
          ...success,
          variant: success.variant || "success",
        }),
      )
    }
    if (invalidateTags?.length && invalidateTagsFn) {
      dispatch(invalidateTagsFn(invalidateTags))
    }
  } catch {
    // errors are handled by the caller, no automatic toast
  }
}

export const getFormattedDate = (date: Date | string): string => {
  return dayjs(date).format(appConstants.dateFormat)
}

export const getOneLineAddress = (address: Address): string => {
  const oneLineAddress = [
    address.streetNumber,
    address.streetName,
    address.city,
    address.postcode,
  ]
    .filter(Boolean)
    .join(", ")

  if (oneLineAddress) {
    return oneLineAddress
  } else {
    return address.addressOneLine || ""
  }
}

export const groupByDetail = (
  list: any[],
  key: string,
): { [key: string]: any[] } => {
  return list.reduce((acc, item) => {
    if (!acc[item[key]]) {
      acc[item[key]] = []
    }
    acc[item[key]].push(item)
    return acc
  }, {})
}

export const convertKeyToCamelCase = (obj: any): object => {
  if (typeof obj !== "object" || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeyToCamelCase(item))
  }

  const newObj: any = {}
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = snakeToCamelCase(key)
      newObj[newKey] = convertKeyToCamelCase(obj[key])
    }
  }
  return newObj
}

const snakeToCamelCase = (str: string) => {
  return str.replace(/(_\w)/g, (match) => match[1].toUpperCase())
}

const employeeColorPalette = [
  "#4E79A7", // steel blue
  "#F28E2B", // tangerine
  "#E15759", // coral red
  "#76B7B2", // teal
  "#59A14F", // fern green
  "#EDC948", // goldenrod
  "#B07AA1", // mauve
  "#FF9DA7", // blush pink
  "#9C755F", // warm taupe
  "#BAB0AC", // pewter
  "#AF7AA1", // plum
  "#4DB6AC", // mint
  "#FF7043", // burnt orange
  "#7986CB", // periwinkle
  "#AED581", // lime
  "#4DD0E1", // cyan
  "#DCE775", // chartreuse
  "#FFB74D", // apricot
  "#A1887F", // mocha
  "#90A4AE", // slate
]

export const getPresetColor = (index: number) => {
  return employeeColorPalette[index % employeeColorPalette.length]
}

const providerColorPalette = [
  "#8B5CF6", // vivid purple
  "#0891B2", // deep cyan
  "#D946EF", // magenta
  "#0D9488", // dark teal
  "#6D28D9", // indigo
  "#DB2777", // hot pink
  "#0E7490", // ocean blue
  "#7C3AED", // violet
  "#059669", // emerald
  "#C026D3", // fuchsia
]

export const getProviderColor = (index: number) => {
  return providerColorPalette[index % providerColorPalette.length]
}

export const getRandomHexColor = () => {
  return `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`
}

export const getFullName = (firstName: string, lastName: string) => {
  return [firstName, lastName].filter(Boolean).join(" ")
}

export const getCalculatedTime = (timeStr: string | null): number | null => {
  if (!timeStr) return timeStr as null
  const [hour, minute] = timeStr.split(":").map(Number)
  return hour + minute / 60
}

export const getHourFormat = (calculatedTime: string) => {
  return dayjs(calculatedTime).format("H:mm")
}

export const formattedCurrency = (amount: number, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount)
}

export const getMapLinkUrlByAddress = (oneLineAddress: string) => {
  return `https://www.google.com/maps?q=${encodeURIComponent(oneLineAddress)}`
}

export const isDeliveryOnTime = (order: Order): boolean => {
  let isOnTime = false
  if (
    order.calculatedArrivalTime &&
    (order.desiredArrivalTimeHour || order.desiredArrivalTimeHourEnd)
  ) {
    const arrivalTimeFormat = getHourFormat(order.calculatedArrivalTime)
    const deliveryTime = getCalculatedTime(arrivalTimeFormat) as number

    if (order.desiredArrivalTimeHour && order.desiredArrivalTimeHourEnd) {
      const startTime = getCalculatedTime(
        order.desiredArrivalTimeHour,
      ) as number
      const endTime = getCalculatedTime(
        order.desiredArrivalTimeHourEnd,
      ) as number

      if (deliveryTime >= startTime && deliveryTime <= endTime) {
        isOnTime = true
      }
    } else if (order.desiredArrivalTimeHour) {
      isOnTime = true
    } else if (order.desiredArrivalTimeHourEnd) {
      const calculatedEnd = getCalculatedTime(
        order.desiredArrivalTimeHourEnd,
      ) as number

      if (deliveryTime <= calculatedEnd) {
        isOnTime = true
      }
    }
  } else if (
    order.calculatedArrivalTime &&
    !order.desiredArrivalTimeHour &&
    !order.desiredArrivalTimeHourEnd
  ) {
    isOnTime = true
  }
  return isOnTime
}

export const getEmployeeOrders = (
  orderList: OrderViewModel[],
  employeeList: HaulerEmployee[],
) => {
  // Single pass: bucket orders by haulerEmployeeId
  const buckets = new Map<
    number,
    { grouped: Record<number, OrderViewModel[]>; individual: OrderViewModel[]; onTime: number; delayed: number }
  >()

  for (const order of orderList) {
    if (!order.haulerEmployeeId) continue
    let bucket = buckets.get(order.haulerEmployeeId)
    if (!bucket) {
      bucket = { grouped: {}, individual: [], onTime: 0, delayed: 0 }
      buckets.set(order.haulerEmployeeId, bucket)
    }

    if (order.isOnTimeDelivery) {
      bucket.onTime++
    } else {
      bucket.delayed++
    }

    if (order.routeSummaryId) {
      if (!bucket.grouped[order.routeSummaryId]) {
        bucket.grouped[order.routeSummaryId] = []
      }
      bucket.grouped[order.routeSummaryId].push(order)
    } else {
      bucket.individual.push(order)
    }
  }

  // Sort grouped orders by sequence
  const employeeOrders: Record<number, AssignedEmployeeOrderDetail> = {}
  for (const [empId, bucket] of buckets) {
    for (const routeId of Object.keys(bucket.grouped)) {
      bucket.grouped[Number(routeId)].sort((a: Order, b: Order) => {
        if (a.sequence == null) return 1
        if (b.sequence == null) return -1
        return a.sequence - b.sequence
      })
    }
    employeeOrders[empId] = {
      grouped: bucket.grouped,
      individual: bucket.individual,
      onTimeOrders: bucket.onTime,
      delayedOrders: bucket.delayed,
    }
  }

  // Ensure every employee has an entry
  for (const haulerEmployee of employeeList) {
    if (!employeeOrders[haulerEmployee.id]) {
      employeeOrders[haulerEmployee.id] = defaultEmployeeOrders
    }
  }

  return employeeOrders
}

export const GetEmployeeOrderDetail = (
  orderList: OrderViewModel[],
  haulerEmployeeId: number,
): AssignedEmployeeOrderDetail => {
  const employeeOrderList = orderList.filter(
    (e) => e.haulerEmployeeId === haulerEmployeeId,
  )
  const orderListWithRoute = employeeOrderList.filter((e) => e.routeSummaryId)
  const groupedOrdersMap = groupByDetail(orderListWithRoute, "routeSummaryId")

  let onTimeOrders = 0
  let delayedOrders = 0

  const setOrderStatusCount = (order: OrderViewModel) => {
    if (order.isOnTimeDelivery) {
      onTimeOrders++
    } else {
      delayedOrders++
    }
  }

  Object.keys(groupedOrdersMap).forEach((routeSummaryId: string) => {
    groupedOrdersMap[routeSummaryId].forEach((order: OrderViewModel) => {
      setOrderStatusCount(order)
    })
    groupedOrdersMap[routeSummaryId].sort((a: Order, b: Order) => {
      if (a.sequence == null) return 1
      if (b.sequence == null) return -1
      return a.sequence - b.sequence
    })
  })

  const orderListWithoutRoute = employeeOrderList.filter(
    (e) => !e.routeSummaryId,
  )

  orderListWithoutRoute.forEach((order) => {
    setOrderStatusCount(order)
  })

  return {
    grouped: groupedOrdersMap,
    individual: orderListWithoutRoute,
    onTimeOrders: onTimeOrders,
    delayedOrders: delayedOrders,
  }
}

export const orderSortingFunction = (sortBy: string, sortOrder: string) => {
  return (a: any, b: any) => {
    let value1 = a[sortBy]
    let value2 = b[sortBy]
    if (typeof value1 === "string" && typeof value2 === "string") {
      return value1.localeCompare(value2)
    }
    if (value1 > value2) return sortOrder === "asc" ? 1 : -1
    if (value1 < value2) return sortOrder === "asc" ? -1 : 1
    return 0
  }
}

export const employeeSortingFunction = (
  sortBy: string,
  sortOrder: SortOrderType,
) => {
  return (a: HaulerEmployee, b: HaulerEmployee) => {
    let value1 = a[sortBy as keyof HaulerEmployee] || ""
    let value2 = b[sortBy as keyof HaulerEmployee] || ""
    if (sortBy === "employeeName") {
      value1 = getFullName(a.firstName, a.lastName)
      value2 = getFullName(b.firstName, b.lastName)
    }
    if (typeof value1 === "string" && typeof value2 === "string") {
      return value1.localeCompare(value2)
    }
    if (value1 > value2) return sortOrder === "asc" ? 1 : -1
    if (value1 < value2) return sortOrder === "asc" ? -1 : 1
    return 0
  }
}

export const getDataFromLocalStorage = (key: string) => {
  const getValue = localStorage.getItem(key) as any
  return JSON.parse(getValue)
}

export const setDataInLocalStorage = (key: string, value: any) => {
  const modifiedValue = JSON.stringify(value)
  return localStorage.setItem(key, modifiedValue)
}

export const extractResponseData = <T>(response: { data: T }): T => {
  return response.data
}
